import { TEXT_SIZE } from '@/typography/Typography';
import {
    TextField,
    TextFieldProps,
    Typography,
    InputAdornment,
    Box
} from '@mui/material';
import styled from '@emotion/styled';
import { staticColors } from '@/lib/theme';
import { EditIcon } from '@/icons/EditIcon';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

export const TypographyContainer = styled(Typography)`
    :after {
        content: attr(data-text);
        color: ${staticColors.error};
        position: absolute;
    }
`;

const TextBox = styled(TextField)`
    /* Chrome, Safari, Edge, Opera */
    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    /* Firefox */
    input[type='number'] {
        -moz-appearance: textfield;
    }
`;

export const InputText = ({
    testid: _testid,
    label,
    placeholder,
    error,
    value,
    onChange,
    required,
    helperText,
    inputAdornment,
    spacing,
    disabled,
    allowDisabledEdit,
    inputStyles,
    onDisabledEditClick,
    containerStyles,
    ...props
}: TextFieldProps & {
    testid?: string;
    inputAdornment?: React.ReactNode;
    spacing?: string;
    allowDisabledEdit?: boolean;
    onDisabledEditClick?: () => void;
    inputStyles?: any;
    containerStyles?: any;
}) => {
    return (
        <Box
            style={{ margin: spacing, ...containerStyles }}
            position={'relative'}
        >
            <TypographyContainer
                fontWeight={500}
                fontSize={TEXT_SIZE.MEDIUM_SMALL}
                data-text={required ? '*' : ''}
            >
                {label}
            </TypographyContainer>
            <TextBox
                fullWidth
                value={value ?? ''}
                placeholder={placeholder}
                size="small"
                disabled={disabled}
                id="outlined-basic"
                variant="outlined"
                onChange={onChange}
                error={error}
                helperText={helperText}
                slotProps={{
                    input: {
                        sx: { borderRadius: '8px', ...inputStyles },
                        endAdornment: (
                            <InputAdornment position="end">
                                {inputAdornment as any}
                            </InputAdornment>
                        )
                    },
                    htmlInput: {
                        'aria-label':
                            typeof label === 'string' ? label : undefined
                    }
                }}
                sx={{
                    margin: '3px 0',
                    ...(disabled &&
                        !error && {
                            backgroundColor: '#f5f5f5',
                            color: '#a1a1a1',
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#D0D5DD'
                            }
                        }),
                    ...(error && {
                        backgroundColor: '#FEF3F2',
                        '& .MuiOutlinedInput-input': {
                            color: staticColors.error,
                            WebkitTextFillColor: staticColors.error
                        },
                        '& .MuiInputLabel-root': {
                            color: staticColors.error
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: `${staticColors.error} !important`
                        },
                        '& .Mui-disabled .MuiOutlinedInput-notchedOutline': {
                            borderColor: `${staticColors.error} !important`
                        }
                    })
                }}
                {...props}
            />
            {disabled && allowDisabledEdit && (
                <div
                    style={{
                        position: 'absolute',
                        right: '10px',
                        top: '32px',
                        cursor: 'pointer'
                    }}
                    onClick={onDisabledEditClick}
                >
                    <EditIcon />
                </div>
            )}

            {error && (
                <div
                    style={{
                        position: 'absolute',
                        right: '10px',
                        top: '32px',
                        cursor: 'pointer'
                    }}
                    onClick={onDisabledEditClick}
                >
                    <ErrorOutlineIcon color={'error'} />
                </div>
            )}
        </Box>
    );
};
